import { ESharedFunction } from "motw_core/Enums/ESharedFunction";
import { fromOthersNodeName } from "motw_core/Repositories/WorkflowRepository";
import * as React from "react";
import { HandleBookOfMindKeyboardEvents } from "source/app/presentation/MainPanel/Workflows/BookOfMind/Display/Effects/HandleBookOfMindKeyboardEvents";
import { LoadAllBookOfMindQuotesChronologically } from "source/app/presentation/MainPanel/Workflows/BookOfMind/Display/Effects/LoadAllBookOfMindQuotesChronologically";
import { LoadBookOfMindQuotesForDisplay } from "source/app/presentation/MainPanel/Workflows/BookOfMind/Display/Effects/LoadBookOfMindQuotesForDisplay";
import { PlaySoundOnScrollingInBookOfMind } from "source/app/presentation/MainPanel/Workflows/BookOfMind/Display/Effects/PlaySoundOnScrollingInBookOfMind";
import { DisplayQuotes } from "source/app/presentation/MainPanel/Workflows/BookOfMind/Display/Quotes/DisplayQuotes";
import { IBookOfMindState } from "source/app/presentation/MainPanel/Workflows/BookOfMind/Helpers/IBookOfMindState";
import { NoSearchResultsText } from "source/app/presentation/MainPanel/Workflows/BookOfMind/NoSearchResultsText/NoSearchResultsText";
import { NumberOfVerses } from "source/app/presentation/MainPanel/Workflows/BookOfMind/NumberOfVerses/NumberOfVerses";
import { Style, StyleClass } from "source/app/presentation/Shared/Styles/CSS";
import { bookOfMindKeyboardEvents } from "source/app/services/UI/KeyboardEvents/BookOfMindKeyboardEvents";
import { WithKeyboardHotkeys } from "source/app/services/UI/KeyboardEvents/WithKeyboardHotkeys";
import { ShareState } from "source/app/services/UI/State/ShareState";
import { bookOfMindSpeech } from "../Speech/BookOfMindSpeech";
import { LoadingSpinner } from "../../../Calendar/LoadingSpinner";
import { Quote } from "source/app/presentation/Shared/Quote/Quote";
import MotwLogicServer from "source/app/services/External/LogicServer/MotwLogicServer";
import * as styles from "../BookOfMind.css";
// import useNoSleep from "use-no-sleep";

export function DisplayBookOfMind({ rootNodeName }: { rootNodeName: string }) {
    const [state, setState] = React.useState<IBookOfMindState>({
        displayOrder: "random",
        refreshTimeStamp: Date.now(),
        searchText: "",
        chronologicalQuotes: [],
        remainingQuotesToDisplay: [],
        totalQuotesToDisplay: 0,
        numberOfQuotesToDisplay: 20,
        selectedQuote: undefined,
        isLoadingRelatedQuotes: false,
        isSpeechStarting: false,
        isSpeechActive: false,
        spokenQuoteIndex: undefined,
        spokenWordStartIndex: undefined,
        spokenWordEndIndex: undefined,
        itemsToSpeak: [],
        rootNodeName,
        readingListNodeNames: [rootNodeName],
    });

    ShareState(ESharedFunction.BookOfMind, state, setState);
    LoadAllBookOfMindQuotesChronologically(state.readingListNodeNames);
    LoadBookOfMindQuotesForDisplay(state);
    HandleBookOfMindKeyboardEvents(state);
    PlaySoundOnScrollingInBookOfMind(state);
    OnDisplayedQuotesChanged(state, rootNodeName);
    WarmUpRelatedQuotes();
    // useNoSleep(true);

    const containerClass = StyleClass(`bookOfMind`, {
        fontFamily: "Montaga",
        marginLeft: `0px`,
    });

    const isWaitingForQuotes =
        state.isLoadingRelatedQuotes ||
        (state.remainingQuotesToDisplay.length === 0 &&
            state.searchText === "");

    if (isWaitingForQuotes) {
        return (
            <div className={containerClass}>
                <div
                    style={{
                        display: "grid",
                        placeItems: "center",
                        height: "62vh",
                    }}
                >
                    <LoadingSpinner
                        className={styles.bookOfMindLoadingSpinner}
                    />
                </div>
            </div>
        );
    }

    return (
        <WithKeyboardHotkeys
            name="bookofMind"
            keyboardEvents={bookOfMindKeyboardEvents}
        >
            <div className={containerClass}>
                <div
                    {...Style("bookOfMindTitleSpacing", {
                        paddingTop: "11px",
                        marginBottom: "-4px",
                    })}
                >
                    <Quote>
                        {state.rootNodeName === fromOthersNodeName
                            ? "The Book of Mind"
                            : state.rootNodeName}
                    </Quote>
                </div>
                <NumberOfVerses
                    numVerses={state.remainingQuotesToDisplay.length}
                    numberOfQuotesToDisplay={state.numberOfQuotesToDisplay}
                    totalQuotesToDisplay={state.totalQuotesToDisplay}
                    rootNodeName={rootNodeName}
                    state={state}
                />
                <NoSearchResultsText
                    searchText={state.searchText}
                    displayedQuotes={state.remainingQuotesToDisplay}
                />
                <DisplayQuotes
                    {...{
                        state,
                    }}
                />
            </div>
        </WithKeyboardHotkeys>
    );
}

// Fire-and-forget: ask the server to precompute the related-quotes index in
// the background so the first "related" click is fast. Runs once per mount.
function WarmUpRelatedQuotes() {
    React.useEffect(() => {
        MotwLogicServer.WarmUpBookOfMindRelatedQuotes().catch(() => {
            // Best-effort warm-up; ignore failures so it never blocks the UI.
        });
    }, []);
}

function OnDisplayedQuotesChanged(
    state: IBookOfMindState,
    rootNodeName: string
) {
    React.useEffect(() => {
        bookOfMindSpeech.StopSpeech();
        const resetScroll = () => {
            const container = document.getElementById("childContainer");

            if (container) {
                container.scrollTop = 0;
            }
        };

        resetScroll();
        const animationFrame = window.requestAnimationFrame(resetScroll);

        return () => window.cancelAnimationFrame(animationFrame);
    }, [state.remainingQuotesToDisplay, rootNodeName]);
}
